import React from 'react';
import classnames from 'classnames';
import { defineMessages, useIntl } from 'react-intl';
import { IconButton } from 'dig-components/buttons';
import { DeleteLine, UndoLine } from 'dig-components/icons/src';
import { Menu } from 'dig-components/menu';
import { Tooltip } from 'dig-components/tooltips';
import { Text } from 'dig-components/typography';
import { noop } from 'lodash';

import * as types from '../constants/types';
import { GenericObject } from 'common/types/generic-object';

const messages = defineMessages({
  removal: {
    id: '',
    description: 'text in team access modal indicating the email access to template will be revoked and can be undone with the link',
    defaultMessage: '“{entityLabel}” will be removed',
  },
  notAdded: {
    id: '',
    description: 'text in team access modal indicating the email will not be added to template accessor list and can be undone with the link',
    defaultMessage: '“{entityLabel}” will not be added',
  },
  removeAdminOrCreator: {
    id: '',
    description: 'Template creators and admin cannot be removed from a template',
    defaultMessage: 'Template creators and admin cannot be removed from a template',
  },
  admin: {
    id: '',
    description: 'text in right side of email in list of template accessor pane of team access modal indicating admin have access to template',
    defaultMessage: 'Admin',
  },
  owner: {
    id: '',
    description: 'text in right side of email in list of template accessor pane of team access modal indicating creator have access to template',
    defaultMessage: 'Creator',
  },
  self: {
    id: '',
    description: 'text in right side of email in list of template accessor pane of team access modal indicating the current user has access to template',
    defaultMessage: 'Self',
  },
});

interface Props {
  entity: GenericObject;
  isEditable: boolean;
  grantAccess: Function;
  revokeAccess: Function;
  resetAccess: Function;
}

/**
 * Renders a team access modal access list entity.
 */
export function EntityAccessSelectedListItem(props: Props) {
  const { entity, isEditable, grantAccess, revokeAccess, resetAccess } = props;
  const {
    hasExplicitTemplateAccess,
    isAccessDirty,
    isAdmin,
    isOwner,
    isSelf,
    type,
    willAccessBeGranted,
    willAccessBeRevoked,
  } = entity;

  const intl = useIntl();
  const entityIsTeam = React.useMemo(() => type === types.TEAM, [type]);
  const entityLabel = React.useMemo(() => (
    entityIsTeam
      ? `${entity.name} (Team)`
      : entity.email
  ), [entityIsTeam, entity]);

  const grantEntityAccess = React.useCallback((evt) => {
    evt.preventDefault();

    grantAccess(entity);
  }, [entity, grantAccess]);

  const revokeEntityAccess = React.useCallback((evt) => {
    evt.preventDefault();

    revokeAccess(entity);
  }, [entity, revokeAccess]);

  const resetEntityAccess = React.useCallback((evt) => {
    evt.preventDefault();

    resetAccess(entity);
  }, [entity, resetAccess]);

  function getEntityRowProps(entity: GenericObject) {
    if (entityIsTeam) {
      return {
        key: entity.guid,
        withTitle: <Text isBold={true}>{entity.name} <Text isBold={false} color='faint'>(team)</Text></Text>,
        className: 'team-access-modal__entity-access-selected-list-item',
      };
    } else {
      const nameAsTitle = !!entity.name;

      return {
        key: entity.guid,
        withTitle: nameAsTitle ? entity.name : entity.email,
        withSubtitle: nameAsTitle ? entity.email : null,
        className: 'team-access-modal__entity-access-selected-list-item',
      };
    }
  }

  let showUndoOption = false;
  let undoAction = resetEntityAccess;
  let removeAction = revokeEntityAccess;
  let undoMessage = messages.removal;

  if (isEditable && isAccessDirty) {
    if (hasExplicitTemplateAccess) {
      if (willAccessBeRevoked) {
        showUndoOption = true;
      }
    } else if (willAccessBeGranted) {
      removeAction = resetEntityAccess;
    } else {
      undoMessage = messages.notAdded;
      undoAction = grantEntityAccess;
      showUndoOption = true;
    }
  }

  const cannotRemove = isAdmin || isOwner || isSelf;
  if (showUndoOption && !cannotRemove) {
    return (
      <Menu.Row
        key={entity.guid}
        preferComposition={true}
        className='team-access-modal__entity-access-selected-list-item'
      >
        <Menu.RowContent>
          <Menu.RowSubtitle>
            {intl.formatMessage(undoMessage, { entityLabel })}
          </Menu.RowSubtitle>
        </Menu.RowContent>
        <Menu.RowAccessory>
          <IconButton onClick={undoAction} variant='transparent' className='team-access-modal__entity-undo'>
            <UndoLine width={24} />
          </IconButton>
        </Menu.RowAccessory>
      </Menu.Row>
    );
  }

  removeAction = cannotRemove ? noop : removeAction;
  const tooltipTitle = intl.formatMessage(messages.removeAdminOrCreator);
  const deleteProps = {
    onClick: removeAction,
    tabIndex: cannotRemove ? 0 : undefined,
  };
  const noClickCls = 'team-access-modal__entity_disabled_delete-wrapper';

  let rightAccessory: React.ReactNode;
  if (isAdmin) {
    rightAccessory = intl.formatMessage(messages.admin);
  } else if (isOwner) {
    rightAccessory = intl.formatMessage(messages.owner);
  } else {
    // wrap DeleteLine in IconButton to avoid issue with tooltip positioning
    // for span wrapper reason, see https://dig.pp.dropbox.com/library/components/tooltips/engineering#disabled-elements
    rightAccessory = isSelf ? null : (
      <span {...deleteProps} className={classnames({ [noClickCls]: cannotRemove })}>
        <IconButton
          disabled={cannotRemove}
          variant='transparent'
          className={classnames({ 'team-access-modal__entity_disabled_delete': cannotRemove })}
        >
          <DeleteLine width={24} />
        </IconButton>
      </span>
    );
  }

  return (
    <Menu.Row
      {...getEntityRowProps(entity)}
      withRightAccessory={
        cannotRemove ? <Tooltip placement='left' title={tooltipTitle}><Text className={noClickCls} size='small'>{rightAccessory}</Text></Tooltip> : rightAccessory
      }
    >
    </Menu.Row>
  );
}
